import React from "react";
import { Card, CardContent, Typography, Box, CircularProgress, IconButton, Tooltip } from "@mui/material";
import {
  Star as StarIcon,
  StarHalf as StarHalfIcon,
  Apple as AppleIcon,
  Android as AndroidIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import "./Dashboard.scss";

const AppStoreReviews = ({ ratings, loading }) => {
  // Store URLs
  const APPLE_APP_STORE_URL = "https://apps.apple.com/us/app/the-tapping-solution/id1419815487";
  const GOOGLE_PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.datechnologies.tappingsolution";

  const renderStars = (rating, isLarge = false) => {
    const stars = [];
    const decimal = rating - Math.floor(rating);

    // Determine star display based on decimal value
    let fullStars, hasHalfStar;
    if (decimal >= 0.75) {
      // Round up (e.g., 4.8 → 5 stars)
      fullStars = Math.ceil(rating);
      hasHalfStar = false;
    } else if (decimal >= 0.25) {
      // Show half star (e.g., 4.5 → 4.5 stars)
      fullStars = Math.floor(rating);
      hasHalfStar = true;
    } else {
      // Round down (e.g., 4.2 → 4 stars)
      fullStars = Math.floor(rating);
      hasHalfStar = false;
    }

    const emptyStars = hasHalfStar ? 5 - fullStars - 1 : 5 - fullStars;

    // Full stars
    for (let i = 0; i < fullStars; i++) {
      stars.push(<StarIcon key={`full-${i}`} className="star filled" fontSize="small" />);
    }

    // Half star
    if (hasHalfStar) {
      stars.push(<StarHalfIcon key="half" className="star filled" fontSize="small" />);
    }

    // Empty stars
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<StarIcon key={`empty-${i}`} className="star" fontSize="small" />);
    }

    return <Box className={isLarge ? "star-rating" : "review-stars"}>{stars}</Box>;
  };

  const renderStoreRatingSummary = (store, icon) => {
    const storeData = ratings?.[store];
    const storeUrl = store === "apple" ? APPLE_APP_STORE_URL : GOOGLE_PLAY_STORE_URL;
    const storeName = store === "apple" ? "App Store" : "Google Play";

    // For Google Play, use approximate display string; for Apple, use exact count
    const getReviewCountDisplay = () => {
      if (store === "google" && storeData?.total_reviews_display) {
        return `~${storeData.total_reviews_display}`;
      }
      return storeData?.total_reviews?.toLocaleString() || "0";
    };

    return (
      <Box className="store-rating-summary">
        <Box className="store-header">
          {icon}
          <Typography variant="subtitle1" className="store-name">
            {storeName}
          </Typography>
          <Tooltip title={`Open ${storeName} listing`} arrow>
            <IconButton
              size="small"
              onClick={() => window.open(storeUrl, "_blank")}
              sx={{ ml: "auto", color: "#048CB9" }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
          <Typography variant="h3" sx={{ color: "#048CB9", fontWeight: 700 }}>
            {storeData?.average_rating?.toFixed(1) || "0.0"}
          </Typography>
          <Box>
            {renderStars(storeData?.average_rating || 0, true)}
            <Typography variant="body2" sx={{ color: "#666", mt: 0.5 }}>
              {getReviewCountDisplay()} reviews
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderReview = (review, index) => {
    // FIX: Generate unique key using multiple fields + index to prevent duplicates
    // Previous approach (author + date) could fail if:
    // - Multiple reviews from same author on same date
    // - Either value is undefined (produces "undefined" + "undefined")
    const uniqueKey = `${review.author || "anon"}-${review.date || Date.now()}-${review.content?.substring(0, 20) || ""}-${index}`;
    
    return (
      <Box className="review-item" key={uniqueKey}>
        <Box className="review-header">
          <Typography variant="subtitle2" className="review-author">
            {review.author}
          </Typography>
          {renderStars(review.rating)}
        </Box>
        {review.title && (
          <Typography variant="body2" className="review-title">
            {review.title}
          </Typography>
        )}
        <Typography variant="body2" className="review-content">
          {review.content}
        </Typography>
        {review.date && (
          <Typography variant="caption" className="review-date">
            {new Date(review.date).toLocaleDateString()}
          </Typography>
        )}
      </Box>
    );
  };

  const renderStoreSection = (store, icon) => {
    const storeData = ratings?.[store];
    const reviews = storeData?.reviews || [];

    return (
      <Box className="store-section">
        {/* Rating Summary */}
        {renderStoreRatingSummary(store, icon)}

        {/* Recent Reviews */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#666", fontWeight: 600 }}>
            Recent Reviews
          </Typography>
          <Box className="reviews-list">
            {reviews.length > 0 ? (
              reviews.slice(0, 5).map((review, index) => renderReview(review, index))
            ) : (
              <Typography variant="body2" className="no-reviews">
                No recent reviews available
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Card className="app-store-reviews-widget">
      <CardContent>
        <Typography variant="h6" className="widget-title">
          App Store Ratings & Reviews
        </Typography>
        {loading ? (
          <Box className="loading-container">
            <CircularProgress />
          </Box>
        ) : (
          <Box className="reviews-container">
            {renderStoreSection("apple", <AppleIcon className="store-icon" />)}
            {renderStoreSection("google", <AndroidIcon className="store-icon" />)}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default AppStoreReviews;
